import React, { useMemo, useState } from 'react';
import { Button, Input, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../../variables/api/axiosInstance';
import CBInputNumber from '../../../components/CBInputNumber';

/* ─────────────────────────────────────────────────────────────────────────
   Nombre de victimes déclaré - version « dossier ouvert ».

   UNE BANDE D'UNE LIGNE, ET NON UNE CARTE. Deux nombres n'ont jamais justifié
   un en-tête à icône, une grille, un bandeau de total et un bouton en
   permanence : cela occupait près de trois cents pixels de haut sur un onglet
   dont l'essentiel est le tableau des personnes. La bande reprend la forme de
   `CarteAssure`, juste au-dessus d'elle sur le même écran - même fond, même
   bordure, même étiquette à gauche et même bouton discret à droite.

   La saisie ne s'ouvre QUE sur demande. Au repos on lit le bilan ; « Modifier »
   déplie les deux champs et le motif, sur place. Le formulaire ne prend donc de
   la place qu'au moment où l'on s'en sert.

   Elle écrit via PATCH /v1/sinistres/{id}/bilan-victimes, qui met à jour EN
   PLACE : l'URL de l'écran reste valide après l'enregistrement.

   Un blessé est un blessé : deux nombres seulement. Le degré d'atteinte relève
   de l'expertise médicale, pas de la déclaration.

   Le motif n'est exigé que si l'un des deux nombres change réellement - même
   règle que côté backend, pour que l'enregistrement ne soit jamais refusé par
   surprise.
   ───────────────────────────────────────────────────────────────────────── */

const GREEN = '#1a6b3c';
const INK_MUTED = '#6b7280';
const DANGER = '#d92d20';
const WARN = '#b54708';

const BANDE = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
  padding: '12px 16px', marginBottom: 16, borderRadius: 12,
  background: '#f8fafc', border: '1px solid #e5e7eb',
};

const num = (v) => Number(v) || 0;

export default function BilanVictimesCard({ sinistreId, bilan, nbFiches = 0, onUpdated }) {
  const { t } = useTranslation('dossierFinance');
  const initial = useMemo(() => ({
    blesses: num(bilan?.blesses),
    decedes: num(bilan?.decedes),
  }), [bilan]);

  const [edition, setEdition] = useState(false);
  const [v, setV] = useState(initial);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const totalInitial = initial.blesses + initial.decedes;
  const total = num(v.blesses) + num(v.decedes);
  const modifie = num(v.blesses) !== initial.blesses || num(v.decedes) !== initial.decedes;

  const set = (cle) => (val) => { setV((p) => ({ ...p, [cle]: val ?? 0 })); setErreur(null); };

  const ouvrir = () => { setV(initial); setMotif(''); setErreur(null); setEdition(true); };
  const fermer = () => { setV(initial); setMotif(''); setErreur(null); setEdition(false); };

  const enregistrer = async () => {
    if (modifie && motif.trim().length < 5) {
      setErreur(t('bilanVictimes.motifTropCourt'));
      return;
    }
    setEnCours(true);
    try {
      await axiosInstance.patch(`/v1/sinistres/${sinistreId}/bilan-victimes`, {
        nombreBlessesAttendu: num(v.blesses) || null,
        nombreDecedesAttendu: num(v.decedes) || null,
        motif: motif.trim() || null,
      });
      toast.success(t('bilanVictimes.misAJour'));
      setMotif('');
      setEdition(false);
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('bilanVictimes.miseAJourImpossible'));
    } finally {
      setEnCours(false);
    }
  };

  /* L'écart entre le nombre déclaré et les fiches saisies. Il ne se calcule
     qu'une fois un nombre déclaré : sans bilan, il n'y a pas d'écart mais une
     saisie à faire, ce que dit le message dédié. */
  const ecart = totalInitial > 0 ? nbFiches - totalInitial : 0;
  const rapprochement = totalInitial === 0
    ? { texte: t('bilanVictimes.renseignerNombre'), couleur: WARN }
    : ecart > 0
      ? { texte: t('bilanVictimes.tropDeFiches', { nombre: nbFiches }), couleur: DANGER }
      : ecart < 0
        ? { texte: t('bilanVictimes.fichesManquantes', { nombre: nbFiches, manque: -ecart }), couleur: WARN }
        : { texte: t('bilanVictimes.fichesSaisies', { nombre: nbFiches }), couleur: INK_MUTED };

  const champ = (cle, libelle) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <CBInputNumber
        min={0} max={999}
        value={v[cle]}
        onChange={set(cle)}
        style={{ width: 72 }}
        placeholder="0"
      />
      <span style={{ fontSize: 12.5, color: INK_MUTED }}>{libelle}</span>
    </span>
  );

  return (
    <div style={BANDE}>
      <Tag color="green" style={{ borderRadius: 20, margin: 0, fontWeight: 600 }}>
        {t('bilanVictimes.victimesDeclarees')}
      </Tag>

      {!edition ? (
        <>
          <span style={{ fontWeight: 600 }}>
            {totalInitial > 0 ? totalInitial : t('bilanVictimes.aucuneDeclaree')}
          </span>
          {totalInitial > 0 && (
            <span style={{ fontSize: 12.5, color: INK_MUTED }}>
              {t('bilanVictimes.resume', { blesses: initial.blesses, deces: initial.decedes })}
            </span>
          )}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: rapprochement.couleur }}>{rapprochement.texte}</span>
            <Button size="small" onClick={ouvrir}>
              {totalInitial > 0 ? t('bilanVictimes.modifier') : t('bilanVictimes.renseigner')}
            </Button>
          </span>
        </>
      ) : (
        <>
          {champ('blesses', t('bilanVictimes.blesses'))}
          {champ('decedes', t('bilanVictimes.decedes'))}
          <span style={{ fontSize: 12.5, color: INK_MUTED }}>
            {t('bilanVictimes.totalDeclare')}&nbsp;
            <strong style={{ color: GREEN, fontSize: 14 }}>{total}</strong>
          </span>
          <span style={{ marginLeft: 'auto' }}>
            <Space size={8}>
              <Button size="small" onClick={fermer} disabled={enCours}>
                {t('bilanVictimes.annuler')}
              </Button>
              <Button
                size="small" type="primary" loading={enCours}
                disabled={!modifie}
                onClick={enregistrer}
                style={modifie ? { background: GREEN, borderColor: GREEN } : undefined}
              >
                {t('bilanVictimes.enregistrer')}
              </Button>
            </Space>
          </span>

          {/* Le motif ne se demande qu'une fois un nombre réellement changé :
              l'exiger d'emblée ferait remplir un champ pour rien. */}
          {modifie && (
            <div style={{ flexBasis: '100%' }}>
              <Input.TextArea
                rows={2}
                maxLength={500}
                showCount
                value={motif}
                onChange={(e) => { setMotif(e.target.value); setErreur(null); }}
                placeholder={t('bilanVictimes.motifExemple')}
                style={{ borderRadius: 8, borderColor: '#e7e9ee' }}
              />
              {erreur && <div style={{ fontSize: 12, color: DANGER, marginTop: 6 }}>{erreur}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
